/**
 * Photo Gallery Component
 *
 * Displays project progress photos in a responsive grid with lightbox
 *
 * @module pages/progress/PhotoGallery
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { getProgressPhotos } from '../../services/progressService';
import { listProjects } from '../../services/projectService';
import { formatDate } from '../../utils/formatters';
import ImageLightbox from '../../components/common/ImageLightbox';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';

const PhotoGallery = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canUploadPhotos = profile?.role !== 'client';
  const [searchParams] = useSearchParams();

  const [selectedProjectFilter, setSelectedProjectFilter] = useState(projectId || searchParams.get('project') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('to') || '');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Fetch projects for filter
  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-gallery'],
    queryFn: () => listProjects({ pageSize: 100 }),
  });

  const projects = projectsData?.data || [];

  // Fetch photos
  const { data: photosData, isLoading } = useQuery({
    queryKey: ['progress-photos', selectedProjectFilter, dateFrom, dateTo],
    queryFn: () =>
      selectedProjectFilter
        ? getProgressPhotos(selectedProjectFilter, { limit: 100 })
        : Promise.resolve({ data: [], count: 0 }),
    enabled: !!selectedProjectFilter,
  });

  const photos = photosData?.data || [];

  // Filter by date range (client-side)
  const filteredPhotos = photos.filter((photo) => {
    if (dateFrom && new Date(photo.uploaded_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(photo.uploaded_at) > new Date(dateTo)) return false;
    return true;
  });

  const handleProjectChange = (projectId) => {
    setSelectedProjectFilter(projectId);
    if (projectId) {
      navigate(`/progreso/galeria/${projectId}`);
    } else {
      navigate('/progreso/galeria');
    }
  };

  const handleLightboxClose = () => {
    setLightboxIndex(null);
  };

  const handleLightboxNext = () => {
    if (lightboxIndex !== null && lightboxIndex < filteredPhotos.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  const handleLightboxPrev = () => {
    if (lightboxIndex !== null && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h3 mb-0">Galería de Progreso</h1>
              <p className="text-muted mb-0">
                {filteredPhotos.length} foto{filteredPhotos.length !== 1 ? 's' : ''}
              </p>
            </div>
            {canUploadPhotos && (
              <Button
                variant="primary"
                onClick={() => navigate('/progreso')}
              >
                <i className="bi bi-cloud-upload me-2"></i>
                Subir Fotos
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Proyecto</Form.Label>
            <Form.Select
              value={selectedProjectFilter}
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              <option value="">Seleccionar proyecto...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.project_number} - {project.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Desde</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Hasta</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando fotos...</span>
          </Spinner>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !selectedProjectFilter && (
        <Card className="text-center py-5">
          <Card.Body>
            <i className="bi bi-images fs-1 text-muted mb-3 d-block"></i>
            <h5>Selecciona un Proyecto</h5>
            <p className="text-muted mb-0">
              Elige un proyecto del filtro para ver sus fotos de progreso
            </p>
          </Card.Body>
        </Card>
      )}

      {/* No Photos State */}
      {!isLoading && selectedProjectFilter && filteredPhotos.length === 0 && (
        <Card className="text-center py-5">
          <Card.Body>
            <i className="bi bi-image fs-1 text-muted mb-3 d-block"></i>
            <h5>No hay Fotos</h5>
            <p className="text-muted mb-3">
              {dateFrom || dateTo
                ? 'No se encontraron fotos en el rango de fechas seleccionado'
                : 'Este proyecto aún no tiene fotos de progreso'}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate(`/progreso?projectId=${selectedProjectFilter}`)}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              Subir Primera Foto
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Photo Grid */}
      {!isLoading && filteredPhotos.length > 0 && (
        <Row className="g-3">
          {filteredPhotos.map((photo, index) => (
            <Col key={photo.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                className="h-100 cursor-pointer photo-card"
                onClick={() => setLightboxIndex(index)}
                style={{ cursor: 'pointer' }}
              >
                <div
                  style={{
                    height: '200px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Card.Img
                    variant="top"
                    src={photo.photo_url}
                    alt={photo.caption || 'Progress photo'}
                    style={{
                      height: '200px',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                    className="photo-hover"
                  />
                  {photo.gps_latitude && photo.gps_longitude && (
                    <Badge
                      bg="dark"
                      className="position-absolute top-0 end-0 m-2"
                      style={{ opacity: 0.8 }}
                    >
                      <i className="bi bi-geo-alt"></i>
                    </Badge>
                  )}
                </div>
                <Card.Body className="p-2">
                  <small className="text-muted d-block mb-1">
                    <i className="bi bi-calendar me-1"></i>
                    {formatDate(photo.uploaded_at)}
                  </small>
                  {photo.caption && (
                    <small className="d-block text-truncate" title={photo.caption}>
                      {photo.caption}
                    </small>
                  )}
                  {photo.uploaded_by_profile?.full_name && (
                    <small className="text-muted d-block text-truncate">
                      <i className="bi bi-person me-1"></i>
                      {photo.uploaded_by_profile.full_name}
                    </small>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={filteredPhotos}
          currentIndex={lightboxIndex}
          onClose={handleLightboxClose}
          onNext={handleLightboxNext}
          onPrev={handleLightboxPrev}
        />
      )}

      <style jsx>{`
        .photo-card:hover .photo-hover {
          transform: scale(1.05);
        }
      `}</style>
      </Container>
    </DashboardLayout>
  );
};

export default PhotoGallery;
